import type { JsonObject } from "@/lib/api/types/json";

/** Single key used in normalized aggregate points for formula / catalog metrics. */
export const DERIVED_SERIES_KEY = "__derived";

/** Canonical widget telemetry binding + optional semantic layer (formula or metric_id). */
export type StandardWidgetDataConfig = {
  device_id: string;
  time_range: TimeRangeKey;
  aggregation: AggregationKey;
  /**
   * Key into per-bucket `values` for charts and stat/gauge readers.
   * Raw channel mode: equals `channel`. Formula / metric mode: {@link DERIVED_SERIES_KEY}.
   */
  series_key: string;
  /** Raw telemetry channel (mutually exclusive with formula + metric_id). */
  channel?: string;
  /** Inline formula (mutually exclusive with channel and metric_id). */
  formula?: string;
  /** Tenant catalog metric (mutually exclusive with channel and formula). */
  metric_id?: string;
  /** Optional display title in builder. */
  title?: string;
  /** Polling interval in seconds (10–120). */
  refresh_interval_seconds?: number;
  /** Gauge scale (optional). */
  min_value?: number;
  max_value?: number;
};

export type TimeRangeKey = "last_5m" | "last_1h" | "last_24h" | "last_7d";
export type DashboardRangeKey = TimeRangeKey | "custom";

export type DashboardTimeRangeSelection =
  | { key: TimeRangeKey; start?: undefined; end?: undefined }
  | { key: "custom"; start: string; end: string };

export type AggregationKey = "avg" | "min" | "max";

const TIME_RANGES = new Set<TimeRangeKey>(["last_5m", "last_1h", "last_24h", "last_7d"]);
const AGGREGATIONS = new Set<AggregationKey>(["avg", "min", "max"]);

const METRIC_KEY_RE = /^[a-zA-Z][a-zA-Z0-9_]{0,63}$/;

const RESERVED = new Set(["avg", "min", "max"]);

const UUID_RE =
  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[1-8][0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}$/;

export type ParsedWidgetConfig =
  | { ok: true; data: StandardWidgetDataConfig }
  | { ok: false; error: string };

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

export function collectFormulaIdentifiers(formula: string): string[] {
  const tokens = formula.match(/\b[a-zA-Z_][a-zA-Z0-9_]*\b/g) ?? [];
  return [...new Set(tokens.filter((t) => !RESERVED.has(t)))];
}

function balancedParens(s: string): boolean {
  let depth = 0;
  for (const c of s) {
    if (c === "(") {
      depth += 1;
    } else if (c === ")") {
      depth -= 1;
      if (depth < 0) {
        return false;
      }
    }
  }
  return depth === 0;
}

export type ParseWidgetConfigOptions = {
  /** When set, formula identifiers must be a subset of these channel keys. */
  channelKeys?: string[];
};

export function parseStandardWidgetConfig(raw: unknown, options?: ParseWidgetConfigOptions): ParsedWidgetConfig {
  if (!isRecord(raw)) {
    return { ok: false, error: "Widget config must be a JSON object." };
  }
  const device_id = raw.device_id;
  const channelRaw = raw.channel;
  const formulaRaw = raw.formula;
  const metric_id_raw = raw.metric_id;
  const time_range = raw.time_range;
  const aggregation = raw.aggregation;

  if (typeof device_id !== "string" || !device_id.trim()) {
    return { ok: false, error: "config.device_id is required." };
  }

  const channel =
    typeof channelRaw === "string" && channelRaw.trim()
      ? channelRaw.trim()
      : undefined;
  const formula =
    typeof formulaRaw === "string" && formulaRaw.trim() ? formulaRaw.trim() : undefined;
  const metric_id =
    typeof metric_id_raw === "string" && metric_id_raw.trim() ? metric_id_raw.trim() : undefined;

  const hasCh = Boolean(channel);
  const hasF = Boolean(formula);
  const hasM = Boolean(metric_id);

  if (hasM && hasF) {
    return { ok: false, error: "Use only one of metric_id or inline formula." };
  }
  if (hasCh && (hasM || hasF)) {
    return { ok: false, error: "Cannot combine channel with formula or metric_id." };
  }
  if (!hasCh && !hasM && !hasF) {
    return { ok: false, error: "Provide channel for raw telemetry, formula, or metric_id." };
  }

  if (hasCh) {
    if (!METRIC_KEY_RE.test(channel!)) {
      return { ok: false, error: "config.channel must be a valid metric key." };
    }
  }
  if (hasM && !UUID_RE.test(metric_id!)) {
    return { ok: false, error: "config.metric_id must be a valid UUID." };
  }
  if (hasF) {
    if (!balancedParens(formula!)) {
      return { ok: false, error: "Formula has unbalanced parentheses." };
    }
    const idents = collectFormulaIdentifiers(formula!);
    if (idents.length === 0) {
      return { ok: false, error: "Formula must reference at least one channel identifier." };
    }
    if (options?.channelKeys?.length) {
      const allowed = new Set(options.channelKeys);
      for (const id of idents) {
        if (!allowed.has(id)) {
          return { ok: false, error: `Unknown channel reference in formula: ${id}` };
        }
      }
    }
  }

  if (typeof time_range !== "string" || !TIME_RANGES.has(time_range as TimeRangeKey)) {
    return {
      ok: false,
      error: "config.time_range must be one of: last_5m, last_1h, last_24h, last_7d.",
    };
  }
  if (typeof aggregation !== "string" || !AGGREGATIONS.has(aggregation as AggregationKey)) {
    return {
      ok: false,
      error: "config.aggregation must be one of: avg, min, max.",
    };
  }

  const title = raw.title;
  const refresh_interval_seconds = raw.refresh_interval_seconds;
  const min_value = raw.min_value;
  const max_value = raw.max_value;

  const series_key = hasCh ? channel! : DERIVED_SERIES_KEY;

  const data: StandardWidgetDataConfig = {
    device_id: device_id.trim(),
    time_range: time_range as TimeRangeKey,
    aggregation: aggregation as AggregationKey,
    series_key,
  };

  if (hasCh) {
    data.channel = channel;
  }
  if (hasF) {
    data.formula = formula;
  }
  if (hasM) {
    data.metric_id = metric_id;
  }

  if (typeof title === "string" && title.trim()) {
    data.title = title.trim();
  }
  if (typeof refresh_interval_seconds === "number" && Number.isFinite(refresh_interval_seconds)) {
    data.refresh_interval_seconds = refresh_interval_seconds;
  }
  if (typeof min_value === "number" && Number.isFinite(min_value)) {
    data.min_value = min_value;
  }
  if (typeof max_value === "number" && Number.isFinite(max_value)) {
    data.max_value = max_value;
  }

  return { ok: true, data };
}

export function configFromJsonObject(config: JsonObject | null | undefined): unknown {
  return config ?? {};
}

/** Window length in ms for telemetry queries. */
export function timeRangeToDurationMs(tr: TimeRangeKey): number {
  switch (tr) {
    case "last_5m":
      return 300_000;
    case "last_1h":
      return 3_600_000;
    case "last_24h":
      return 86_400_000;
    case "last_7d":
      return 7 * 86_400_000;
    default:
      return 86_400_000;
  }
}

export function isFixedTimeRangeKey(value: DashboardRangeKey | null | undefined): value is TimeRangeKey {
  return value === "last_5m" || value === "last_1h" || value === "last_24h" || value === "last_7d";
}

export function rangeSelectionToDurationMs(range: DashboardTimeRangeSelection): number {
  if (range.key !== "custom") {
    return timeRangeToDurationMs(range.key);
  }
  const start = new Date(range.start).getTime();
  const end = new Date(range.end).getTime();
  if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) {
    return timeRangeToDurationMs("last_24h");
  }
  return end - start;
}

export function clampRefreshIntervalMs(seconds: number | undefined): number {
  const s = seconds ?? 10;
  const clamped = Math.min(120, Math.max(10, s));
  return clamped * 1000;
}

export function bucketSecondsForRange(durationMs: number): number {
  if (durationMs <= 3_600_000) {
    return 120;
  }
  if (durationMs <= 86_400_000) {
    return 300;
  }
  return 3600;
}
